import { useI18n } from "@/lib/i18n/useI18n";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPie } from "@fortawesome/free-solid-svg-icons";
import { Button, Chip } from "@heroui/react";
import { ListApplications, SectorSummary } from "@/types/operations";
import { roundTo } from "@/lib/utils/numbers";
import SummaryTable from "@/components/field/operations/SummaryTable";
import SectorTable from "@/components/field/operations/SectorTable";
import { UseQueryResult } from "@tanstack/react-query";

function ApplicationSummary({
    applicationOperationsQuery,
    sectorSummaryQuery,
}: {
    fieldId?: number;
    selectedCropCycle?: { start_date: string; end_date: string };
    applicationOperationsQuery: UseQueryResult<ListApplications>;
    sectorSummaryQuery: UseQueryResult<SectorSummary[]>;
}) {
    const { t } = useTranslation("operations");
    const { formatUnit } = useI18n();
    const [viewSectorSummary, setViewSectorSummary] = useState(false);


    const totalCompleteN = useMemo(() => {
        return applicationOperationsQuery.data?.operations
            .filter((operation) => operation.is_complete === true)
            .reduce((sum, operation) => sum + (operation.average_N || 0), 0);
    }, [applicationOperationsQuery.data?.operations]);

    const totalPendingN = useMemo(() => {
        return applicationOperationsQuery.data?.operations
            .filter((operation) => operation.is_complete === false)
            .reduce((sum, operation) => sum + (operation.average_N || 0), 0);
    }, [applicationOperationsQuery.data?.operations]);

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <div className="font-semibold text-lg flex flex-row items-center gap-2">
                    <Button
                        isIconOnly
                        variant="solid"
                        color="primary"
                        aria-label="Sector Summary"
                        isLoading={sectorSummaryQuery.isLoading}
                        size="sm"
                        onPress={() => setViewSectorSummary(!viewSectorSummary)}
                    >
                        <FontAwesomeIcon icon={faChartPie} />
                    </Button>
                    {t("applications")}
                </div>
                {applicationOperationsQuery.data && (
                    <div className="flex flex-row items-end gap-1">
                        <Chip color="success" variant="flat">
                            {t("total_n_applied")}:{" "}
                            {totalCompleteN ? roundTo(totalCompleteN, 1) : "0"}{" "}
                            {formatUnit("lb_ac", "kg_ha")}
                        </Chip>
                        <Chip color="warning" variant="flat">
                            {t("total_n_pending")}:{" "}
                            {totalPendingN ? roundTo(totalPendingN, 1) : "0"}{" "}
                            {formatUnit("lb_ac", "kg_ha")}
                        </Chip>
                    </div>
                )}
            </div>
            {viewSectorSummary ? (
                <SectorTable sectorQuery={sectorSummaryQuery} />
            ) : (
                <SummaryTable operationsQuery={applicationOperationsQuery} />
            )}
        </>
    );
}

export default ApplicationSummary;
